import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { GraphFact } from '@/components/Charts/types';
import { getChartDataWithBenchmarks } from '@/components/Charts/utils';
import { WaterHistoricalChart } from '@/components/Charts/WaterHistorical';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { getBenchmarksFromFacts } from '@/pages/shared/hooks/useBenchmarkForFact';

import { NoPlotData } from '../NoPlotData';

export const WaterHistoricalGraphTile = ({
  graphData,
  benchmarkFact,
  activeCustomBenchmark,
  selectedBenchmarkId,
  onBenchmarkSelect,
}: {
  graphData: GraphFact[] | null | undefined;
  benchmarkFact: {
    high: PlotReportFactElement<number | null> | undefined;
    moderate: PlotReportFactElement<number | null> | undefined;
    low: PlotReportFactElement<number | null> | undefined;
  };
  activeCustomBenchmark?: CustomBenchmarkSuccessResponse | null;
  selectedBenchmarkId: string | null;
  onBenchmarkSelect: (id: string | null) => void;
}) => {
  const { t } = useTranslation();
  const { analysisType } = useNCCardContext();

  const { zoom, setZoom } = useChartZoom();

  const whcBenchmarks = getBenchmarksFromFacts({
    highBenchmarkFact: benchmarkFact.high,
    moderateBenchmarkFact: benchmarkFact.moderate,
    lowBenchmarkFact: benchmarkFact.low,
  });

  const noChartData = !graphData?.length;

  if (noChartData) {
    return <NoPlotData />;
  }

  const chartData = getChartDataWithBenchmarks({
    chartData: graphData,
    benchmarks: benchmarkFact,
  });

  return (
    <Stack
      spacing={6}
      className='items-center justify-center px-1 py-6 lg:items-start lg:p-6 lg:pl-1'
      data-testid='water-historical-graph-tile'
    >
      <ChartHeading
        // TODO: MVP-3284 Update copy for historicalGraphHeadline
        heading={t('shared.ncaDetail.details.water.labels.historicalGraphHeadline')}
        popover={{
          title: t('shared.ncaDetail.details.water.explainers.historicalData.title'),
          body: t('shared.ncaDetail.details.water.explainers.historicalData.body'),
        }}
        className='w-full px-6 sm:px-0'
        zoom={zoom}
        setZoom={setZoom}
        indicator={GraphTileType.historic_water_soil_moisture}
        selectedBenchmark={selectedBenchmarkId}
        setSelectedBenchmark={onBenchmarkSelect}
      />
      <WaterHistoricalChart
        analysisType={analysisType}
        data={chartData}
        benchmarks={whcBenchmarks}
        customBenchmark={activeCustomBenchmark}
        showTooltip={true}
        className='relative'
        data-testid='water-historical-chart'
        zoom={zoom}
        setZoom={setZoom}
      />
    </Stack>
  );
};
